import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a product image modal slider.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The slide media elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {import('swiper').Swiper | undefined} */
  #swiper;

  /** @type {Element | null} */
  #previousButton;

  /** @type {Element | null} */
  #nextButton;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('click', this.#handleDialogOutsideClick);
    this.#initSwiper();
  }

  disconnectedCallback() {
    this.refs.dialog.removeEventListener('click', this.#handleDialogOutsideClick);
    this.#swiper?.destroy(true, true);
    super.disconnectedCallback();
  }

  #initSwiper() {
    if (!window.Swiper) return;

    const swiperElement = this.querySelector('[data-zoom-dialog-swiper]');
    if (!swiperElement) return;

    const nextEl = this.querySelector('[data-zoom-dialog-next]');
    const prevEl = this.querySelector('[data-zoom-dialog-prev]');
    const paginationEl = this.querySelector('[data-zoom-dialog-pagination]');
    this.#nextButton = nextEl;
    this.#previousButton = prevEl;

    this.#swiper = new window.Swiper(swiperElement, {
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
      pagination: paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : undefined,
      on: {
        slideChange: () => this.#handleActiveIndex(this.#swiper?.activeIndex ?? 0),
        reachBeginning: () => this.#updateArrowVisibility(),
        reachEnd: () => this.#updateArrowVisibility(),
        fromEdge: () => this.#updateArrowVisibility(),
        resize: () => this.#updateArrowVisibility(),
      },
    });

    this.#updateArrowVisibility();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event?.preventDefault();

    const targetIndex = this.#normalizeIndex(index);
    this.refs.dialog.showModal();
    this.#selectSlide(targetIndex, 0);
  }

  /**
   * Loads a high-resolution image for a specific media container.
   *
   * @param {HTMLElement} mediaContainer - The media container element.
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (dialog.open) dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  #handleDialogOutsideClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const panel = this.querySelector('[data-zoom-dialog-panel]');

    if (panel?.contains(event.target)) return;

    if (event.target === this.refs.dialog || event.target.closest('[data-zoom-dialog-overlay]')) {
      this.close();
    }
  };

  /**
   * Closes the dialog when the user presses escape and changes slides with arrow keys.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#swiper?.slidePrev();
      this.#updateArrowVisibility();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#swiper?.slideNext();
      this.#updateArrowVisibility();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   *
   * @param {number} index - The index of the thumbnail to select.
   */
  handleThumbnailClick(index) {
    this.#selectSlide(index);
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   *
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  handleThumbnailPointerEnter(index) {
    const target = this.refs.media[this.#normalizeIndex(index)];
    if (target) this.#loadHighResolutionImage(target);
  }

  #selectSlide(index, speed = 300) {
    const targetIndex = this.#normalizeIndex(index);

    if (this.#swiper) {
      this.#swiper.update();

      if (this.#swiper.activeIndex !== targetIndex) {
        this.#swiper.slideTo(targetIndex, speed);
        this.#updateArrowVisibility();
        return;
      }
    }

    this.#handleActiveIndex(targetIndex);
  }

  #handleActiveIndex(index) {
    const targetIndex = this.#normalizeIndex(index);
    const target = this.refs.media[targetIndex];

    if (!target) return;

    this.#loadHighResolutionImage(target);
    this.dispatchEvent(new ZoomMediaSelectedEvent(targetIndex));
    this.#updateArrowVisibility();
  }

  #updateArrowVisibility() {
    const mediaLength = this.refs.media?.length ?? 0;

    this.#setArrowHidden(this.#previousButton, mediaLength <= 1 || !this.#swiper || this.#swiper.isBeginning);
    this.#setArrowHidden(this.#nextButton, mediaLength <= 1 || !this.#swiper || this.#swiper.isEnd);
  }

  /**
   * @param {Element | null} button
   * @param {boolean} hidden
   */
  #setArrowHidden(button, hidden) {
    if (!button) return;

    button.classList.toggle('tw-hidden', hidden);
    button.toggleAttribute('disabled', hidden);
    button.setAttribute('aria-hidden', `${hidden}`);
  }

  #normalizeIndex(index) {
    const mediaLength = this.refs.media?.length ?? 0;
    const numericIndex = Number(index);

    if (!mediaLength || Number.isNaN(numericIndex)) return 0;

    return Math.min(Math.max(numericIndex, 0), mediaLength - 1);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
